import { Badge, Table } from "flowbite-react";
import TablePagination from "./Pagination";
import { DataGridPagination } from "@/sharedTypes";
import { HiInbox } from "react-icons/hi";
import { useTranslation } from "react-i18next";

interface DataTableProps {
  columns: { key: string; label: string; render?: (row: any) => JSX.Element }[];
  data: { [key: string]: any }[];
  pagination: DataGridPagination;
  onPageChange: (newPageIndex: number) => void;
}

function DataTable({
  columns,
  data,
  pagination,
  onPageChange,
}: DataTableProps) {
  const { t } = useTranslation("common");

  return (
    <div className="flex flex-col gap-4">
      <div className="relative overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <Table className="w-full text-left text-sm text-gray-500 rtl:text-right dark:text-gray-400">
          <Table.Head className="border-b border-gray-200 bg-gray-50/80 text-xs uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
            {columns?.map((column) => (
              <Table.HeadCell
                key={column.key}
                className="px-4 py-3 font-semibold"
              >
                {column.label}
              </Table.HeadCell>
            ))}
          </Table.Head>
          <Table.Body className="divide-y divide-gray-100 bg-white dark:divide-gray-700/50 dark:bg-gray-800">
            {data?.length > 0 ? (
              data.map((row, rowIndex) => (
                <Table.Row
                  key={rowIndex}
                  className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  {columns?.map((column) => (
                    <Table.Cell
                      key={column.key}
                      className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300"
                    >
                      {column.render ? (
                        column.render(row)
                      ) : column.key === "status" ? (
                        <Badge
                          color={
                            row[column.key]?.toLowerCase() === "completed"
                              ? "success"
                              : row[column.key]?.toLowerCase() === "resolved"
                                ? "blue"
                                : row[column.key]?.toLowerCase() === "open"
                                  ? "warning"
                                  : row[column.key]?.toLowerCase() === "closed"
                                    ? "dark"
                                    : "failure"
                          }
                          className="inline-flex w-fit items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        >
                          {row[column.key]}
                        </Badge>
                      ) : (
                        row[column.key]
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell
                  colSpan={columns.length}
                  className="py-16 text-center"
                >
                  <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                    <HiInbox className="mb-3 h-12 w-12" />
                    <p className="text-base font-medium">{t("noDataAvailable", "No data available")}</p>
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      {data?.length > 0 && (
        <div className="flex justify-end px-1">
          <TablePagination pagination={pagination} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
}

export default DataTable;
